import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, gql } from '@apollo/client';
import { useHelpTopics } from '../../../store/useHelpTopics';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronRight, Plus, Save } from 'lucide-react';
import { useUser } from '@/store/useUser';
import { hasAnyPolicy } from '@/lib/policies';

// GraphQL Queries and Mutations
const GET_HELP_TOPICS = gql`
  query GetHelpTopics {
    listHelpTopics {
      id
      name
    }
  }
`;

const GET_HELP_TOPIC = gql`
  query GetHelpTopic($id: Int!) {
    getHelpTopic(id: $id) {
      id
      name
      content
    }
  }
`;

const CREATE_HELP_TOPIC = gql`
  mutation CreateHelpTopic($name: String!, $content: String!) {
    createHelpTopic(name: $name, content: $content) {
      id
      name
      content
    }
  }
`;

const UPDATE_HELP_TOPIC = gql`
  mutation UpdateHelpTopic($id: Int!, $updates: UpdateHelpTopicInput!) {
    updateHelpTopic(id: $id, updates: $updates) {      
      message
    }
  }
`;

type HTMLInputChangeEvent = {
  target: {
    name: string;
    value: string;
  };
};

type HTMLInputProps = {
  name: string;
  value: string;
  onChange: (event: HTMLInputChangeEvent) => void;
  readOnly?: boolean;
};

const HTMLInput = ({ name, value, onChange, readOnly = false }: HTMLInputProps) => {
  const [html, setHtml] = React.useState(value || '');
  const quillRef = React.useRef(null);

  const handleChange = React.useCallback((content: string) => {
    setHtml(content);
    onChange({ target: { name, value: content } });
  }, [name, onChange]);

  useEffect(() => {
    if (value !== html) {
      setHtml(value || '');
    }
  }, [value, html]);

  return (
    <div className="h-full flex flex-col">
      <ReactQuill
        ref={quillRef}
        value={html}
        onChange={handleChange}
        readOnly={readOnly}
        className="flex-1 bg-white"
        style={{ height: '100%' }}
        modules={{
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link'],
          ]
        }}
      />
    </div>
  );
};

export const HelpTopicsManager = () => {
  const { user } = useUser();
  const [selectedTopic, setSelectedTopic] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [newTopicName, setNewTopicName] = useState('');
  const [newTopicContent, setNewTopicContent] = useState('');
  
  // Simplified approach - only track actual user changes
  const [hasUserMadeChanges, setHasUserMadeChanges] = useState(false);

  const { pushHelpTopic, popHelpTopic } = useHelpTopics();
  
  useEffect(() => {
    pushHelpTopic('HelpTopics');
    return () => {
      popHelpTopic();
    };
  }, [pushHelpTopic, popHelpTopic]);

  const { loading: topicsLoading, error: topicsError, data: topicsData, refetch: refetchTopics } = useQuery(GET_HELP_TOPICS);
  const { loading: topicLoading, error: topicError, data: topicData } = useQuery(GET_HELP_TOPIC, {
    variables: { id: selectedTopic },
    skip: !selectedTopic,
  });

  const [createHelpTopic] = useMutation(CREATE_HELP_TOPIC);
  const [updateHelpTopic] = useMutation(UPDATE_HELP_TOPIC);
  const canCreateTopic = hasAnyPolicy(user, ['admin:access~full']);
  const canUpdateTopic = hasAnyPolicy(user, ['admin:access~full']);

  useEffect(() => {
    if (topicData?.getHelpTopic) {
      const topicName = topicData.getHelpTopic.name || '';
      const topicContent = topicData.getHelpTopic.content || '';
      
      setNewTopicName(topicName);
      setNewTopicContent(topicContent);
      
      // Reset change indicator when loading a topic
      setHasUserMadeChanges(false);
    }
  }, [topicData]);

  const handleCreateTopic = async () => {
    if (!canCreateTopic) return;

    try {
      const result = await createHelpTopic({
        variables: { name: newTopicName, content: newTopicContent },
      });
      
      setIsCreating(false);
      refetchTopics();
      
      // Select the newly created topic
      if (result.data?.createHelpTopic?.id) {
        setSelectedTopic(result.data.createHelpTopic.id);
      }
      
      setHasUserMadeChanges(false);
    } catch (error) {
      console.error('Error creating topic:', error);
    }
  };

  const handleUpdateTopic = async () => {
    if (!selectedTopic || !canUpdateTopic) return;
    
    try {
      await updateHelpTopic({
        variables: {
          id: selectedTopic,
          updates: {
            name: newTopicName,
            content: newTopicContent,
          },
        },
      });
      
      setHasUserMadeChanges(false);
      refetchTopics();
    } catch (error) {
      console.error('Error updating topic:', error);
    }
  };

  const handleSelectTopic = (id: number) => {
    if (hasUserMadeChanges) {
      if (window.confirm('You have unsaved changes. Do you want to discard them?')) {
        setSelectedTopic(id);
        setIsCreating(false);
        setHasUserMadeChanges(false);
      }
      return;
    }
    
    setSelectedTopic(id);
    setIsCreating(false);
  };

  const startCreateTopic = () => {
    if (!canCreateTopic) return;

    if (hasUserMadeChanges) {
      if (window.confirm('You have unsaved changes. Do you want to discard them?')) {
        resetEditorState();
      }
      return;
    }
    
    resetEditorState();
  };
  
  const resetEditorState = () => {
    setIsCreating(true);
    setSelectedTopic(null);
    setNewTopicName('');
    setNewTopicContent('');
    setHasUserMadeChanges(false);
  };

  // Only register explicit changes by user interaction
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (selectedTopic && !canUpdateTopic) return;

    setNewTopicName(e.target.value);
    // Only mark as changed if this was triggered by user input
    if (e.type === 'change') {
      setHasUserMadeChanges(true);
    }
  };

  const handleContentChange = (e: HTMLInputChangeEvent) => {
    if (selectedTopic && !canUpdateTopic) return;

    setNewTopicContent(e.target.value);
    // For ReactQuill, we need a different approach because change events work differently
    // Only set hasUserMadeChanges if there was an actual user interaction
    if (document.activeElement?.tagName === 'TEXTAREA' || 
        document.activeElement?.className.includes('ql-editor')) {
      setHasUserMadeChanges(true);
    }
  };

  if (topicsLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
      </div>
    );
  }

  if (topicsError) {
    return (
      <div className="p-4 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
        Error loading topics: {topicsError.message}
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 h-screen overflow-y-auto">
      <div className="max-w-none mx-auto px-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Help Topics Manager</h1>
              <p className="text-gray-600 mt-1">Create and manage help documentation for users</p>
            </div>
            {canCreateTopic && (
              <button
                onClick={startCreateTopic}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 shadow-sm transition-colors"
              >
                <Plus className="w-4 h-4" />
                New Topic
              </button>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 min-h-[600px]">
          <div className="flex h-[600px]">
            {/* Left Panel - Topic List */}
            <div className="w-96 border-r border-gray-200 flex flex-col">
              <div className="p-4 border-b border-gray-100">
                <h3 className="font-medium text-gray-900">Topics ({topicsData.listHelpTopics.length})</h3>
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {topicsData.listHelpTopics.map((topic: any) => (
                  <button
                    key={topic.id}
                    onClick={() => handleSelectTopic(topic.id)}
                    className={`w-full p-3 text-left hover:bg-gray-50 border-b border-gray-50 flex items-center justify-between group transition-all ${
                      selectedTopic === topic.id ? 'bg-blue-50 border-l-3 border-l-blue-500 text-blue-900' : 'text-gray-700'
                    }`}
                  >
                    <span className="text-sm font-medium truncate">{topic.name}</span>
                    <ChevronRight className={`w-4 h-4 transition-colors ${
                      selectedTopic === topic.id ? 'text-blue-500' : 'text-gray-400 group-hover:text-gray-600'
                    }`} />
                  </button>
                ))}

                {topicsData.listHelpTopics.length === 0 && !isCreating && (
                  <div className="text-center py-16 px-6">
                    <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                      <Plus className="w-8 h-8 text-gray-400" />
                    </div>
                    <h3 className="text-sm font-medium text-gray-900 mb-2">No topics yet</h3>
                    {canCreateTopic && (
                      <>
                        <p className="text-sm text-gray-500 mb-4">Get started by creating your first help topic</p>
                        <button
                          onClick={startCreateTopic}
                          className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Create First Topic
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Right Panel - Topic Content */}
            <div className="flex-1 flex flex-col">
              {isCreating && canCreateTopic ? (
                <>
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">Create New Topic</h2>
                        <p className="text-sm text-gray-500 mt-1">Add a new help topic with content</p>
                      </div>
                      <div className="flex items-center gap-3">
                        <button 
                          onClick={() => {
                            setIsCreating(false);
                            setNewTopicName('');
                            setNewTopicContent('');
                            setHasUserMadeChanges(false);
                          }}
                          className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button 
                          onClick={handleCreateTopic} 
                          disabled={!newTopicName.trim()}
                          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                        >
                          <Save className="w-4 h-4" />
                          Create Topic
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex-1 p-6 overflow-hidden flex flex-col">
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Topic Name</label>
                      <Input
                        type="text"
                        placeholder="Enter a descriptive name for this help topic"
                        value={newTopicName}
                        onChange={handleInputChange}
                        className="w-full"
                      />
                    </div>
                    
                    <div className="flex-1 min-h-0">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                      <HTMLInput
                        name="content"
                        value={newTopicContent}
                        onChange={handleContentChange}
                      />
                    </div>
                  </div>
                </>
              ) : selectedTopic ? (
                topicLoading ? (
                  <div className="flex justify-center items-center h-full">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
                      <p className="text-gray-500">Loading topic...</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="p-6 border-b border-gray-100">
                      <div className="flex items-center justify-between">
                        <div>
                          <h2 className="text-xl font-semibold text-gray-900">
                            {canUpdateTopic ? 'Edit Topic' : 'View Topic'}
                          </h2>
                          <p className="text-sm text-gray-500 mt-1">
                            {canUpdateTopic
                              ? 'Update the content and settings for this topic'
                              : 'Review the content for this topic'}
                          </p>
                        </div>
                        <div className="flex items-center gap-3">
                          <button 
                            onClick={() => {
                              setSelectedTopic(null);
                              setHasUserMadeChanges(false);
                            }}
                            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                          >
                            Close
                          </button>
                          {canUpdateTopic && (
                            <button
                              onClick={handleUpdateTopic}
                              disabled={!hasUserMadeChanges}
                              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors"
                            >
                              <Save className="w-4 h-4" />
                              Save Changes
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex-1 p-6 overflow-hidden flex flex-col">
                      <div className="mb-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Topic Name</label>
                        <Input
                          type="text"
                          placeholder="Enter a descriptive name for this help topic"
                          value={newTopicName}
                          onChange={handleInputChange}
                          disabled={!canUpdateTopic}
                          className="w-full"
                        />
                      </div>
                      
                      <div className="flex-1 min-h-0">
                        <label className="block text-sm font-medium text-gray-700 mb-2">Content</label>
                        <HTMLInput
                          name="content"
                          value={newTopicContent}
                          onChange={handleContentChange}
                          readOnly={!canUpdateTopic}
                        />
                      </div>
                    </div>
                  </>
                )
              ) : (
                <div className="flex flex-col items-center justify-center h-full p-12">
                  <div className="text-center max-w-md">
                    <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                      <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Select a topic to edit</h3>
                    <p className="text-gray-500 mb-6">
                      Choose a topic from the sidebar to view{canUpdateTopic ? ' and edit' : ''} its content.
                    </p>
                    {canCreateTopic && (
                      <button
                        onClick={startCreateTopic}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 mx-auto transition-colors"
                      >
                        <Plus className="w-5 h-5" />
                        Create New Topic
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpTopicsManager;
